import type { AreaPiloto } from "@/types/territorial";
import type { QuadraComAnalise, TreeMapPoint } from "@/types/cta-mapa-ui";
import { ilhaDeCalorAlta } from "@/config/cta-mapa-config";

interface PilotIndicatorsProps {
  area: AreaPiloto;
  quadras: QuadraComAnalise[];
  trees: TreeMapPoint[];
  treesLoaded: boolean;
}

interface KpiItem {
  label: string;
  value: string;
  hint?: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const fmtInt = (n: number) => n.toLocaleString("pt-BR");
const fmtPct = (n: number) => `${n.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

/**
 * Indicadores da area piloto.  Inventario vem do CTA; os de resiliencia vem das inspecoes
 * territoriais (FITA), que sao estimativa visual preliminar.  Sem dado, o indicador mostra "—".
 */
export default function PilotIndicators({ area, quadras, trees, treesLoaded }: PilotIndicatorsProps) {
  const analisadas = quadras.filter((q) => q.analise).map((q) => q.analise!);
  const copa = mean(analisadas.map((a) => a.treeCanopyPercent).filter((v): v is number => v != null));
  const imperm = mean(analisadas.map((a) => a.imperviousAreaPercent).filter((v): v is number => v != null));
  const plantio = analisadas.map((a) => a.plantingPotentialCount).filter((v): v is number => v != null);
  const comIlha = analisadas.filter((a) => a.heatIslandRisk != null);
  const especies = new Set(trees.map((t) => t.speciesId).filter((v) => v != null));
  const riscoAlto = trees.filter((t) => t.classeRisco === "ALTO" || t.classeRisco === "CRITICO").length;
  const temArvores = treesLoaded && trees.length > 0;

  const kpis: KpiItem[] = [
    {
      label: "Quadras mapeadas",
      value: fmtInt(quadras.length),
      hint: area.expectedBlockCount != null ? `de ${area.expectedBlockCount} esperadas` : undefined,
      icon: "ri-grid-line", color: "text-primary-600", bgColor: "bg-primary-50", borderColor: "border-primary-200",
    },
    {
      label: "Exemplares cadastrados",
      value: treesLoaded ? fmtInt(trees.length) : "—",
      hint: area.projectId == null ? "área sem projeto vinculado" : undefined,
      icon: "ri-plant-line", color: "text-accent-600", bgColor: "bg-accent-50", borderColor: "border-accent-200",
    },
    {
      label: "Espécies identificadas",
      value: temArvores ? fmtInt(especies.size) : "—",
      icon: "ri-leaf-line", color: "text-accent-600", bgColor: "bg-accent-50", borderColor: "border-accent-200",
    },
    {
      label: "Risco alto/crítico",
      value: temArvores ? fmtInt(riscoAlto) : "—",
      hint: "última avaliação registrada",
      icon: "ri-alert-line", color: "text-danger-600", bgColor: "bg-danger-50", borderColor: "border-danger-200",
    },
    {
      label: "Quadras com análise FITA",
      value: fmtInt(analisadas.length),
      icon: "ri-image-line", color: "text-secondary-600", bgColor: "bg-secondary-50", borderColor: "border-secondary-200",
    },
    {
      label: "Cobertura de copa média",
      value: copa == null ? "—" : fmtPct(copa),
      hint: "FITA, preliminar",
      icon: "ri-shield-check-line", color: "text-secondary-600", bgColor: "bg-secondary-50", borderColor: "border-secondary-200",
    },
    {
      label: "Impermeabilização média",
      value: imperm == null ? "—" : fmtPct(imperm),
      hint: "FITA, preliminar",
      icon: "ri-building-line", color: "text-warning-600", bgColor: "bg-warning-50", borderColor: "border-warning-200",
    },
    {
      label: "Ilha de calor alta",
      value: comIlha.length ? fmtInt(comIlha.filter((a) => ilhaDeCalorAlta(a.heatIslandRisk)).length) : "—",
      hint: comIlha.length ? `de ${comIlha.length} quadras avaliadas` : "FITA, preliminar",
      icon: "ri-sun-line", color: "text-warning-600", bgColor: "bg-warning-50", borderColor: "border-warning-200",
    },
    {
      label: "Potencial de plantio",
      value: plantio.length ? fmtInt(plantio.reduce((a, b) => a + b, 0)) : "—",
      hint: "vagas estimadas (FITA)",
      icon: "ri-seedling-line", color: "text-primary-600", bgColor: "bg-primary-50", borderColor: "border-primary-200",
    },
  ];

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2">
        {kpis.map((kpi) => (
          <div key={kpi.label} className={`bg-white rounded-card border ${kpi.borderColor} p-3 flex flex-col gap-1.5 hover:shadow-sm transition-shadow`}>
            <div className={`w-7 h-7 rounded-lg ${kpi.bgColor} flex items-center justify-center shrink-0`}>
              <i className={`${kpi.icon} ${kpi.color} text-sm`}></i>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground-900 font-heading leading-tight">{kpi.value}</p>
              <p className="text-[10px] text-foreground-500 leading-tight">{kpi.label}</p>
              {kpi.hint && <p className="text-[9px] text-foreground-400 leading-tight mt-0.5">{kpi.hint}</p>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-foreground-400">
        Indicadores FITA são estimativa visual preliminar a partir de ortoimagem, por quadra; servem para planejar e
        exigem validação em campo.
      </p>
    </div>
  );
}
